import type { MocktailScenario } from '../scenario'
import { isValidScenario } from './scenarioStore'

const STORAGE_KEY = 'mocktail.presets'

export interface MocktailPreset {
  id: string
  name: string
  scenario: MocktailScenario
  savedAt: string
}

function isJsonRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isValidPreset(value: unknown): value is MocktailPreset {
  return (
    isJsonRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.savedAt === 'string' &&
    isValidScenario(value.scenario)
  )
}

// Unlike readScenario's all-or-nothing validation (there's only one active scenario, so a
// malformed blob has nothing sensible to fall back to but the default), this is a list — a single
// corrupted preset shouldn't take the rest of a saved library down with it, so invalid entries are
// just dropped individually.
export function readPresets(): MocktailPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isValidPreset) : []
  } catch {
    return []
  }
}

function writePresets(presets: MocktailPreset[]): void {
  if (presets.length === 0) {
    localStorage.removeItem(STORAGE_KEY)
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets))
  }
}

export function savePreset(
  name: string,
  scenario: MocktailScenario,
): MocktailPreset {
  const preset: MocktailPreset = {
    id: crypto.randomUUID(),
    name,
    scenario,
    savedAt: new Date().toISOString(),
  }
  writePresets([...readPresets(), preset])
  return preset
}

export function deletePreset(id: string): void {
  writePresets(readPresets().filter((preset) => preset.id !== id))
}
